import { logEvent } from './debugLog';
import { voiceLang, getLang } from '../i18n';
import { diagDisabled } from '../utils/diagnostics';
import { featureEnabled } from './venueSettingsService';

/**
 * Voice guidance (Web Speech API) + haptics (navigator.vibrate, Android only
 * — iOS Safari has no vibration API; voice still works there).
 *
 * Design: off by default; the 🗣️ HUD button toggles it. Enabling from a tap
 * also satisfies iOS's user-gesture requirement for speechSynthesis. Prompts
 * are deduped per key with cooldowns so guidance never chatters.
 */

const STORAGE_KEY = 'navme.voice.enabled';

let enabled = localStorage.getItem(STORAGE_KEY) === '1';
const lastSpokenAt = new Map<string, number>();

// ---- 🔊 AI Voice (feature 'aiVoice', Dashboard-gated, default off) ----
// User request: "Can Ask NavMe be in Eleven Labs style" — but explicitly
// did NOT want a paid TTS vendor wired in ("I dont need direct
// ElevenLabs"). This gets a similar "premium, natural" feel for free by
// picking the best voice the DEVICE already ships with instead of
// whatever the browser defaults to — iOS's "Enhanced"/"Premium" Siri
// voices and Android/Chrome's "Google" neural voices both sound
// noticeably more natural than the plain default pick. The Web Speech API
// exposes no real quality metadata, so this is a name/heuristic score, not
// a guarantee — falls back to the browser's normal pick when nothing
// scores higher (silent, same as this feature being off).
let cachedVoices: SpeechSynthesisVoice[] = [];
function refreshVoices(): void {
  cachedVoices = window.speechSynthesis?.getVoices() ?? [];
}
if ('speechSynthesis' in window) {
  refreshVoices();
  // Voices often load asynchronously (esp. Chrome) — getVoices() can
  // return [] on the very first call even though the list fills in
  // moments later.
  window.speechSynthesis.onvoiceschanged = refreshVoices;
}

function pickBestVoice(bcp47: string, shortLang: string): SpeechSynthesisVoice | null {
  if (cachedVoices.length === 0) refreshVoices();
  const candidates = cachedVoices.filter((v) => v.lang.toLowerCase().startsWith(shortLang.toLowerCase()));
  if (candidates.length === 0) return null;
  const score = (v: SpeechSynthesisVoice): number => {
    let s = 0;
    if (v.lang.toLowerCase() === bcp47.toLowerCase()) s += 2; // exact region match (e.g. en-US, not just en-GB)
    if (/enhanced|premium|neural|natural/i.test(v.name)) s += 3; // iOS "Enhanced"/"Premium" Siri voices
    if (/google/i.test(v.name)) s += 2; // Android/Chrome higher-quality voices
    if (!v.localService) s += 1; // network-served voices are often higher fidelity
    return s;
  };
  return candidates.slice().sort((a, b) => score(b) - score(a))[0];
}

export function isVoiceEnabled(): boolean {
  return enabled;
}

export function setVoiceEnabled(on: boolean): void {
  enabled = on;
  localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
  logEvent('session', `voice guidance ${on ? 'on' : 'off'}`);
  if (on) speak('Voice guidance on', 'voice-toggle', 0);
  else window.speechSynthesis?.cancel();
}

/**
 * Speaks `text` unless the same dedupeKey fired within cooldownMs.
 * Cancels any queued utterance first — stale prompts are worse than silence.
 */
export function speak(text: string, dedupeKey = text, cooldownMs = 8000): void {
  if (diagDisabled('voice')) return; // ?no=voice — silence all cues (bare-nav A/B)
  if (!enabled || !('speechSynthesis' in window)) return;
  const now = Date.now();
  if (now - (lastSpokenAt.get(dedupeKey) ?? 0) < cooldownMs) {
    suppressedCount++; // deduped — visible in the debug panel voice line
    return;
  }
  lastSpokenAt.set(dedupeKey, now);

  const u = new SpeechSynthesisUtterance(text);
  u.lang = voiceLang(); // match the selected UI language
  u.rate = 1.05;
  // 🔊 AI Voice: swap in the best-scoring on-device voice + a steadier,
  // more natural pace. No-op (silent fallback to the line above) when the
  // feature is off or no better voice is available for this language.
  const aiVoiceOn = featureEnabled('aiVoice');
  if (aiVoiceOn) {
    const best = pickBestVoice(u.lang, getLang());
    if (best) {
      u.voice = best;
      u.rate = 1.0;
    }
  }
  // LIFECYCLE INSTRUMENTATION (2026-07-13 walk 1: every voice line was
  // logged, yet the screen recording's audio track is DIGITAL SILENCE
  // (max −91 dB) — speak() was called but iOS produced no sound; the user
  // heard nothing all session). `voice:` log lines only prove the REQUEST.
  // These callbacks prove (or disprove) actual audio: no "voice started"
  // after a "voice:" line = iOS swallowed it (silent-ring switch, audio
  // session owned by the camera feed, or missing user-gesture activation).
  u.onstart = () => logEvent('session', `voice started (${text.length} chars)`);
  u.onerror = (e) => logEvent('error', `voice error: ${e.error ?? 'unknown'} — utterance never played`);
  u.onend = () => {
    /* silence is golden — 'started' already proves audio ran */
  };
  // V1 (#25, Test 3): cancel() unconditionally murdered the PREVIOUS cue —
  // "Navigating to Kitchen, 23 meters" was canceled 0 ms later by "Turn
  // ahead in 3 meters" (two 'voice error: canceled' in the log; the route
  // announcement never played). Cues requested within the chatter window
  // QUEUE instead; only after a quiet period is stale backlog cleared.
  const sinceLastRequest = now - lastSpeakRequestAt;
  lastSpeakRequestAt = now;
  if (sinceLastRequest > SPEAK_QUEUE_WINDOW_MS) {
    window.speechSynthesis.cancel();
  }
  window.speechSynthesis.speak(u);
  // iOS quirk: cancel() can leave the queue paused; resume() is a no-op
  // when not paused, and un-wedges synthesis when it is.
  window.speechSynthesis.resume();
  spokenCount++;
  lastUtterance = text;
  logEvent('session', `voice${aiVoiceOn && u.voice ? ` (ai: ${u.voice.name})` : ''}: "${text}"`);
  // One-time state snapshot 1.2 s after the FIRST speak: if it never
  // started, capture what the engine thinks it's doing.
  if (!firstSpeakProbed) {
    firstSpeakProbed = true;
    setTimeout(() => {
      const ss = window.speechSynthesis;
      logEvent(
        'session',
        `voice engine state 1.2s after first speak: speaking=${ss.speaking} pending=${ss.pending} paused=${ss.paused} voices=${ss.getVoices().length}`,
      );
    }, 1200);
  }
}

let firstSpeakProbed = false;
/** Cues within this window of each other queue; after quiet, backlog is cleared. */
const SPEAK_QUEUE_WINDOW_MS = 3000;
let lastSpeakRequestAt = -Infinity;

// ---- Debug-panel line (🐞 in-AR panel) ----
let spokenCount = 0;
let suppressedCount = 0;
let lastUtterance: string | null = null;

/** One-line voice status for the in-AR debug panel. */
export function getVoiceDebugLine(): string {
  const last = lastUtterance ? ` · last "${lastUtterance}"` : '';
  return `voice: ${enabled ? 'ON' : 'off'} · ${spokenCount} spoken / ${suppressedCount} deduped${last}`;
}

/** Short buzz for turns/arrival. No-op where unsupported (iOS). */
export function vibrate(pattern: number | number[]): void {
  if (!enabled) return;
  navigator.vibrate?.(pattern);
}
